import styled from 'styled-components';

import {
    MoreVertOutlined as MoreVertOutlinedIcon,
    GetAppOutlined as GetAppOutlinedIcon
} from '@material-ui/icons';

import logoRedesigned from 'images/logoRedesigned.svg';
import {Button} from "@mui/material";

export const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #fff;
  width: 100%;
  box-sizing: border-box;
  gap: 0.25rem;

  @media (min-width: 480px) {
    padding: 0.5rem 1rem;
    gap: 0.5rem;
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    gap: 1rem;
  }
`

export const ModalHeader = styled.div`
    /* Sticky so flat pages (header + content as siblings in #root) keep the
       header pinned below the Dynamic Island while content scrolls under it.
       No-op when the page wraps content in its own scroll region. (T2-6437) */
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    min-height: 3rem;
    flex-shrink: 0;
    justify-content: space-between;
    /* Tighter horizontal padding on small phones so the back button hugs the
       left margin and the centered title gets more room to sit on one line. */
    padding: 0.75rem 0.75rem;
    /* Use the JS-measured --native-safe-area-top fallback: WKWebView (Cordova)
       often reports env(safe-area-inset-top) as 0, so the notch/Dynamic Island
       overlaps the header without this. See utils/nativeApp.ts. */
    padding-top: calc(0.75rem + max(var(--native-safe-area-top, 0px), env(safe-area-inset-top, 0px)));
    padding-left: calc(0.75rem + env(safe-area-inset-left, 0px));
    padding-right: calc(0.75rem + env(safe-area-inset-right, 0px));
    background-color: #fff;
    box-sizing: border-box;
    width: 100%;
    gap: 0.25rem;

    @media (min-width: 480px) {
        padding: 0.75rem 1rem;
        padding-top: calc(0.75rem + max(var(--native-safe-area-top, 0px), env(safe-area-inset-top, 0px)));
        padding-left: calc(1rem + env(safe-area-inset-left, 0px));
        padding-right: calc(1rem + env(safe-area-inset-right, 0px));
        gap: 0.5rem;
    }

    @media (min-width: 768px) {
        padding: 1rem 2rem;
        padding-top: calc(1rem + max(var(--native-safe-area-top, 0px), env(safe-area-inset-top, 0px)));
        padding-left: calc(2rem + env(safe-area-inset-left, 0px));
        padding-right: calc(2rem + env(safe-area-inset-right, 0px));
    }
`

export const ModalTitle = styled.div`
  position: absolute;
  left: 50%;
  /* The back button and bell are flex children, so align-items:center centers
     them in the CONTENT box (below the safe-area padding-top). This title is
     absolutely positioned, so top:50% is measured against the PADDING box and
     would center it including the safe-area inset — pulling it up under the
     notch / Dynamic Island. Offset the center down by half the safe-area inset
     so it lines up with the side controls (no-op on web where the inset is 0). */
  top: calc(50% + max(var(--native-safe-area-top, 0px), env(safe-area-inset-top, 0px)) / 2);
  transform: translate(-50%, -50%);
  color: var(--text-primary, rgba(0, 0, 0, 0.87));
  text-align: center;
  font-feature-settings: 'clig' off, 'liga' off;
  // T2-5849: bump the base size so page titles ("Shift Details",
  // "Booked/Confirmed Shift", etc.) read with a proper page-header weight on
  // mobile instead of near-caption size.
  // Scale the title with the viewport and keep it on a single row; long titles
  // ("Manually Book a Shift") used to wrap to two lines on narrow screens.
  font-size: clamp(0.95rem, 4.3vw, 1.125rem);
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: 0.15px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  max-width: calc(100% - 7.5rem);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  @media (min-width: 768px) {
    font-size: 1.5rem;
    max-width: calc(100% - 10rem);
  }
`

export const BackBtn = styled(Button)`
    color: var(--primary-main, #FB7672) !important;
    font-size: 0.75rem !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-family: inherit !important;
    text-transform: unset !important;
    border-radius: 1rem !important;
    letter-spacing: 0.4px !important;
    display: flex;
    align-items: center;
    cursor: pointer;
    background: unset;
    border: unset;
    line-height: 1 !important;
    padding: 0.25rem 0 0.25rem 8px !important;
    min-width: auto !important;
    min-height: 44px !important;
    margin: 0 !important;
    flex-shrink: 0;

    &:hover {
        background: unset;
    }

    .arrowIcon {
        height: 0.875rem;
        margin-right: 0.125rem;
        flex-shrink: 0;
    }

    @media (min-width: 360px) {
        font-size: 0.8rem !important;
        padding: 6px 8px !important;
        
        .arrowIcon {
            height: 0.9rem;
            margin-right: 0.2rem;
        }
    }
    
    @media (min-width: 480px) {
        font-size: 0.875rem !important;
        padding-left: 8px !important;
        
        .arrowIcon {
            height: 1rem;
            margin-right: 0.25rem;
        }
    }
    
    @media (min-width: 768px) {
        font-size: 1rem !important;
        padding: 0.25rem 0 0.25rem 12px !important;
        
        .arrowIcon {
            height: 1.25rem;
        }
    }
`

export const BackButtonContainer = styled.div`
  width: 50px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: var(--primary-main, #FB7672);
  flex-shrink: 0;

  @media (min-width: 480px) {
    width: 65px;
    font-size: 14px;
  }
`

export const LogoContainer = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    justify-content: center;
    font-size: var(--typography-h6-size, 1.25rem);
    color: #000;
    font-weight: 600;
    line-height: 150%;
    letter-spacing: 0.5px;
    text-align: center;

    @media (min-width: 480px) {
        font-size: var(--typography-h5-size, 1.5rem);
        line-height: 123.5%;
        letter-spacing: 0.25px;
    }
`

export const LogoImage = styled.img.attrs({
    src: logoRedesigned,
})`
  width: 120px;
  height: auto;
  max-height: 28px;

  @media (min-width: 360px) {
    width: 140px;
  }

  @media (min-width: 480px) {
    width: 160px;
    max-height: 30px;
  }
`

export const AdditionalActionsButton = styled.div`
    display: flex;
    min-width: 32px;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;

    @media (min-width: 480px) {
        min-width: 40px;
    }
`;

export const MobileRightPaddedActions = styled(AdditionalActionsButton)`
  padding-right: 4px;
  padding-right: calc(4px + env(safe-area-inset-right));

  @media (min-width: 480px) {
    padding-right: 8px;
    padding-right: calc(8px + env(safe-area-inset-right));
  }
`;

export const StyledMoreVertOutlinedIcon = styled(MoreVertOutlinedIcon)`
  color: #0B4598;
`

export const StyledGetAppOutlinedIcon = styled(GetAppOutlinedIcon)`
  color: #0B4598;
`
